import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { compressImage, validateImageFile } from '@/utils/imageCompression';
import { uploadToCloudinary } from '@/utils/cloudinary';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const PetImageUpload = () => {
  const { token } = useParams();
  const [status, setStatus] = useState('loading'); // loading | ready | uploading | success | error | expired
  const [clinicId, setClinicId] = useState(null);
  const [errorMsg, setErrorMsg] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    const fetchSession = async () => {
      try {
        const res = await axios.get(`${API_URL}/upload-sessions/${token}`);
        setClinicId(res.data.clinic_id);
        if (res.data.image_url) {
          setPreviewUrl(res.data.image_url);
          setStatus('success');
        } else {
          setStatus('ready');
        }
      } catch (err) {
        const detail = err.response?.data?.detail || '';
        if (err.response?.status === 404 || detail.toLowerCase().includes('expired')) {
          setStatus('expired');
        } else {
          setStatus('error');
          setErrorMsg('Failed to load upload session.');
        }
      }
    };
    fetchSession();
  }, [token]);

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const validation = validateImageFile(file, 3);
    if (!validation.isValid) {
      setErrorMsg(validation.error);
      return;
    }

    setStatus('uploading');
    setErrorMsg('');

    try {
      const compressed = await compressImage(file, {
        maxSizeMB: 3,
        maxWidthOrHeight: 1920,
        quality: 0.8,
      });

      const reader = new FileReader();
      reader.onloadend = () => setPreviewUrl(reader.result);
      reader.readAsDataURL(compressed);

      const imageUrl = await uploadToCloudinary(compressed, {
        clinicId,
        folder: 'pets',
      });

      await axios.put(`${API_URL}/upload-sessions/${token}`, { image_url: imageUrl });
      setStatus('success');
    } catch (err) {
      setStatus('ready');
      setErrorMsg(err.message || 'Upload failed. Please try again.');
    }
  };

  if (status === 'loading') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, border: '4px solid #e5e7eb', borderTopColor: '#5a8a5a', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
          <p style={{ color: '#6b7280', fontSize: 15 }}>Loading...</p>
        </div>
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (status === 'expired') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 340 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>⏰</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>Link Expired</h1>
          <p style={{ color: '#6b7280', lineHeight: 1.6 }}>
            This QR code has expired. Please generate a new one from the desktop app.
          </p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 340 }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>❌</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>Something went wrong</h1>
          <p style={{ color: '#6b7280' }}>{errorMsg || 'Unable to load upload session.'}</p>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f9fafb', padding: 24 }}>
        <div style={{ textAlign: 'center', maxWidth: 340, width: '100%' }}>
          <div style={{ fontSize: 56, marginBottom: 16 }}>✅</div>
          <h1 style={{ fontSize: 24, fontWeight: 700, color: '#1f2937', marginBottom: 8 }}>Photo Uploaded!</h1>
          <p style={{ color: '#6b7280', lineHeight: 1.6, marginBottom: 20 }}>
            Your photo has been uploaded successfully. Return to the desktop to continue.
          </p>
          {previewUrl && (
            <img
              src={previewUrl}
              alt="Uploaded pet"
              style={{ width: '100%', height: 240, objectFit: 'cover', borderRadius: 16, border: '2px solid #e5e7eb' }}
            />
          )}
        </div>
      </div>
    );
  }

  // status === 'ready' | 'uploading'
  return (
    <div style={{ minHeight: '100vh', background: '#f9fafb', padding: 24 }}>
      <div style={{ maxWidth: 380, margin: '0 auto' }}>
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: 32 }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🐾</div>
          <h1 style={{ fontSize: 26, fontWeight: 700, color: '#1f2937', marginBottom: 6 }}>Upload Pet Photo</h1>
          <p style={{ color: '#9ca3af', fontSize: 14 }}>Take a photo or choose from your gallery</p>
        </div>

        {/* Card */}
        <div style={{ background: '#fff', borderRadius: 24, boxShadow: '0 4px 24px rgba(0,0,0,0.08)', padding: 24 }}>
          {status === 'uploading' ? (
            <div style={{ textAlign: 'center', padding: '32px 0' }}>
              <div style={{ width: 48, height: 48, border: '4px solid #e5e7eb', borderTopColor: '#5a8a5a', borderRadius: '50%', animation: 'spin 0.8s linear infinite', margin: '0 auto 16px' }} />
              <p style={{ color: '#374151', fontWeight: 600, fontSize: 16, marginBottom: 4 }}>Uploading photo...</p>
              <p style={{ color: '#9ca3af', fontSize: 13 }}>Please wait</p>
              <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
          ) : (
            <>
              {errorMsg && (
                <div style={{ marginBottom: 16, padding: '12px 14px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 12, color: '#dc2626', fontSize: 13 }}>
                  {errorMsg}
                </div>
              )}

              {/* Two upload options */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
                {/* Camera */}
                <label htmlFor="mobile-camera" style={{ cursor: 'pointer', display: 'block' }}>
                  <div style={{ border: '2px solid #e5e7eb', borderRadius: 16, padding: '20px 12px', textAlign: 'center', background: '#fafafa', transition: 'border-color 0.2s' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>📷</div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: 0 }}>Take Photo</p>
                  </div>
                  <input
                    id="mobile-camera"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </label>

                {/* Gallery */}
                <label htmlFor="mobile-gallery" style={{ cursor: 'pointer', display: 'block' }}>
                  <div style={{ border: '2px solid #e5e7eb', borderRadius: 16, padding: '20px 12px', textAlign: 'center', background: '#fafafa', transition: 'border-color 0.2s' }}>
                    <div style={{ fontSize: 32, marginBottom: 8 }}>🖼️</div>
                    <p style={{ fontSize: 13, fontWeight: 600, color: '#374151', margin: 0 }}>Choose Photo</p>
                  </div>
                  <input
                    id="mobile-gallery"
                    type="file"
                    accept="image/jpeg,image/jpg,image/png,image/webp"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                  />
                </label>
              </div>

              <p style={{ textAlign: 'center', fontSize: 12, color: '#9ca3af' }}>
                JPG, PNG or WebP · Max 3MB · Link expires in 15 min
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
